import * as Api from '@ViewModels';
import {
	type AIConversationCallView,
	useSalesLiveCoachingConversationByIdQuery,
	useSalesLiveCoachingConversationsQuery,
} from './useSalesLiveCoachingConversationsQuery';

type Options = {
	impersonationContext?: Api.IImpersonationContext;
	/** Pass `false` to skip the account-wide list query, which is admin-only and 403s for a manager. */
	preferLoadedList?: boolean;
};

/**
 * Resolves a single coaching conversation's call view for the detail panels, deep-link safe.
 *
 * Prefers the already-loaded list query (`aida/coffee/coaching/filter`, deduped from `ConversationList`)
 * so scrolling to a loaded row costs no extra request. Only when the id is NOT among the loaded pages
 * — the case that matters for a `?convo=<id>` deep link to a conversation past the loaded window —
 * does it fall back to the per-id fetch (`aida/coffee/coaching/{id}`). The fallback stays disabled
 * while the list is still loading its first page, so a freshly-opened deep link waits for the list
 * rather than double-fetching, and it yields to the list view if the row later scrolls in.
 */
export function useCoachingConversationCallView(
	conversationId: string | null,
	options?: Options
): { callView: AIConversationCallView | undefined; isLoading: boolean; notFound: boolean } {
	const { impersonationContext, preferLoadedList = true } = options ?? {};

	const listQuery = useSalesLiveCoachingConversationsQuery({ impersonationContext, enabled: preferLoadedList });
	const listView =
		preferLoadedList && conversationId ? listQuery.data?.callViewsByConversationId.get(conversationId) : undefined;

	// Fall back to the per-id fetch only once the list has settled without the target — never while
	// the first page is still loading (avoids a redundant fetch for an about-to-arrive row).
	const shouldFallback = Boolean(conversationId) && !listView && !(preferLoadedList && listQuery.isLoading);
	const byIdQuery = useSalesLiveCoachingConversationByIdQuery({
		id: conversationId ?? '',
		enabled: shouldFallback,
		impersonationContext,
	});

	const callView = listView ?? byIdQuery.data;
	const isLoading =
		Boolean(conversationId) && !callView && ((preferLoadedList && listQuery.isLoading) || byIdQuery.isLoading);
	// The by-id route 404s when the id doesn't exist or isn't a coaching conversation — surfaced as a
	// dedicated "not found" state (e.g. a stale/invalid `?convo=` deep link) rather than a toast.
	const notFound =
		shouldFallback && byIdQuery.isError && (byIdQuery.error as { systemCode?: number })?.systemCode === 404;

	return { callView, isLoading, notFound };
}
